import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, API, fmtMoney, fmtRange } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, Download, CheckCircle2, ScanSearch, AlertTriangle, ShieldCheck } from "lucide-react";

const PRIO = {
  Critical: "bg-red-100 text-red-700", High: "bg-orange-100 text-orange-700",
  Medium: "bg-amber-100 text-amber-700", Low: "bg-slate-100 text-slate-600",
};

export default function PublicProposal() {
  const { token } = useParams();
  const [p, setP] = useState(undefined); // undefined=loading, null=not found
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/proposals/public/${token}`).then(({ data }) => setP(data)).catch(() => setP(null));
  }, [token]);

  const accept = async () => {
    setErr("");
    if (!name.trim() || !email.trim()) { setErr("Please enter your name and email to accept."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/proposals/public/${token}/accept`, { name, email });
      setP(data.proposal);
      toast.success("Proposal accepted — you can now complete payment.");
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Could not accept this proposal. Please try again.");
    } finally { setBusy(false); }
  };

  const pay = async (mode) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/proposals/public/${token}/checkout`, { mode, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Payment could not be started. Please try again.");
      setBusy(false);
    }
  };

  if (p === undefined) return <div className="min-h-[70vh] grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  if (p === null) return (
    <div className="min-h-[70vh] grid place-items-center px-5">
      <div className="text-center" data-testid="public-proposal-notfound">
        <AlertTriangle className="w-8 h-8 text-slate-300 mx-auto" />
        <p className="mt-3 text-slate-600">This proposal link is invalid or has been removed.</p>
      </div>
    </div>
  );

  const tot = p.totals || {};
  const one = tot.one_time || {};
  const cur = tot.currency || "USD";
  const items = p.line_items || [];
  const accepted = !!p.acceptance;
  const paid = !!p.paid;
  const terminal = ["EXPIRED", "DECLINED"].includes(p.status);

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-slate-50 py-10" data-testid="public-proposal">
      <div className="max-w-3xl mx-auto px-5 sm:px-8">
        <div className="flex items-center gap-2 mb-6">
          <span className="grid place-items-center w-9 h-9 rounded-lg bg-slate-900 text-white"><ScanSearch className="w-5 h-5" /></span>
          <span className="font-heading font-bold text-lg text-slate-900">Opportunity<span className="text-indigo-600">Scanner</span></span>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="bg-slate-900 text-white p-7">
            <p className="text-indigo-300 text-xs font-semibold uppercase tracking-[0.2em]">Proposal for {p.client?.company || p.client?.name || p.business_name}</p>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold mt-1.5" data-testid="public-proposal-title">{p.title}</h1>
            <p className="text-slate-300 text-sm mt-1">{p.business_name}{p.business_address ? ` · ${p.business_address}` : ""}</p>
          </div>

          <div className="p-7">
            {p.client_notes && (
              <div className="mb-6 rounded-xl bg-slate-50 border border-slate-200 p-5">
                <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{p.client_notes}</p>
              </div>
            )}

            <h2 className="font-heading text-lg font-semibold text-slate-900 mb-3">What we'll do</h2>
            <div className="space-y-3" data-testid="public-line-items">
              {items.length === 0 && <p className="text-sm text-slate-400">No items in this proposal yet.</p>}
              {items.map((li, i) => {
                const pr = li.pricing || {};
                return (
                  <div key={i} className="rounded-xl border border-slate-200 p-5" data-testid={`public-item-${i}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-heading font-semibold text-slate-900">{li.name}</p>
                        {li.problem && <p className="text-sm text-slate-500 mt-0.5">{li.problem}</p>}
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-semibold text-slate-900">{fmtRange(pr.price_range, cur)}</div>
                        {pr.recurring && <div className="text-xs text-slate-500">+{fmtMoney(pr.recurring.client_price, cur)}/mo</div>}
                      </div>
                    </div>
                    {li.priority && <span className={`inline-block mt-2 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${PRIO[li.priority] || PRIO.Low}`}>{li.priority}</span>}
                    {li.why_it_matters && <p className="text-sm text-slate-600 mt-2"><span className="font-semibold text-slate-800">Why it matters: </span>{li.why_it_matters}</p>}
                    {(li.deliverables || []).length > 0 && (
                      <ul className="mt-2 space-y-1">
                        {li.deliverables.map((s, j) => (
                          <li key={j} className="flex items-start gap-2 text-sm text-slate-600"><CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" /> {s}</li>
                        ))}
                      </ul>
                    )}
                    {li.expected_outcome && <p className="text-sm text-emerald-700 mt-2"><span className="font-semibold">Expected outcome: </span>{li.expected_outcome}</p>}
                    {li.timeframe && <p className="text-xs text-slate-400 mt-2">Estimated timeline: {li.timeframe}</p>}
                  </div>
                );
              })}
            </div>

            <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-5" data-testid="public-totals">
              <div className="flex items-center justify-between">
                <span className="text-slate-600">One-time investment</span>
                <span className="text-2xl font-bold text-slate-900">{fmtMoney(one.client_price, cur)}</span>
              </div>
              {one.client_price_before_discount && (
                <div className="flex items-center justify-between text-sm text-slate-400 mt-1">
                  <span>Before {one.discount_pct}% discount</span><span className="line-through">{fmtMoney(one.client_price_before_discount, cur)}</span>
                </div>
              )}
              {tot.recurring && (
                <div className="flex items-center justify-between text-sm text-slate-600 mt-2 pt-2 border-t border-slate-200">
                  <span>Ongoing management</span><span className="font-semibold">{fmtMoney(tot.recurring.client_price, cur)}/mo</span>
                </div>
              )}
            </div>

            {/* Acceptance + checkout */}
            {paid ? (
              <div className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50 p-5 flex items-start gap-3" data-testid="public-paid-banner">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold text-emerald-800">Payment received — thank you!</p>
                  <p className="text-sm text-emerald-700 mt-0.5">We'll be in touch shortly to kick things off.</p>
                </div>
              </div>
            ) : terminal ? (
              <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-5 flex items-start gap-3" data-testid="public-terminal-banner">
                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                <p className="text-sm text-amber-800">This proposal is {p.status === "EXPIRED" ? "expired" : "no longer available"}. Please contact us for an updated proposal.</p>
              </div>
            ) : accepted ? (
              <div className="mt-6" data-testid="public-accepted-section">
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex items-center gap-2 mb-4" data-testid="public-accepted-banner">
                  <ShieldCheck className="w-5 h-5 text-emerald-600 shrink-0" />
                  <p className="text-sm text-emerald-800">Accepted by <b>{p.acceptance.name}</b> on {new Date(p.acceptance.accepted_at).toLocaleDateString()}. Complete payment below to get started.</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button onClick={() => pay("full")} disabled={busy} data-testid="public-pay-full" className="bg-slate-900 hover:bg-slate-800">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `Pay in full · ${fmtMoney(one.client_price, cur)}`}
                  </Button>
                  <Button onClick={() => pay("deposit")} disabled={busy} variant="outline" data-testid="public-pay-deposit">
                    Pay 50% deposit · {fmtMoney((one.client_price || 0) * 0.5, cur)}
                  </Button>
                  <Button onClick={() => window.open(`${API}/proposals/public/${token}/pdf`, "_blank")} variant="ghost" data-testid="public-download-pdf">
                    <Download className="w-4 h-4 mr-2" /> PDF
                  </Button>
                </div>
              </div>
            ) : (
              <div className="mt-6 rounded-xl border border-slate-200 p-5" data-testid="public-accept-form">
                <h3 className="font-heading font-semibold text-slate-900">Ready to move forward?</h3>
                <p className="text-sm text-slate-500 mt-0.5 mb-3">Enter your details to accept this proposal, then complete secure checkout.</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Input placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} data-testid="public-accept-name" />
                  <Input type="email" placeholder="Your email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="public-accept-email" />
                </div>
                {err && <p className="text-sm text-red-600 mt-2" data-testid="public-accept-error">{err}</p>}
                <div className="flex flex-wrap gap-2 mt-3">
                  <Button onClick={accept} disabled={busy} data-testid="public-accept-submit" className="bg-slate-900 hover:bg-slate-800">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Review & accept proposal"}
                  </Button>
                  <Button onClick={() => window.open(`${API}/proposals/public/${token}/pdf`, "_blank")} variant="outline" data-testid="public-download-pdf">
                    <Download className="w-4 h-4 mr-2" /> Download PDF
                  </Button>
                </div>
              </div>
            )}

            <p className="mt-6 text-xs text-slate-400 leading-relaxed border-t border-slate-100 pt-5">
              Prices are estimates prepared for {p.business_name || "your business"} and are valid for 30 days unless otherwise stated.
              Any modeled opportunity figures are estimates of potential upside — not guaranteed revenue, savings or ROI.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
